import { SearchCombobox } from '@/features/shared/components/search-combobox/search-combobox';
import { getFullAddressString } from '@/lib/address-helper';
import type { co_getplato_grid_api_domain_Address } from '@/lib/grid/api';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CustomerAddressDTO } from '@/server/dto/customer-address-dto';
import { api } from '@/utils/api';
import { SrCustomerAddressType } from '@plato/data/starrocks/types';
import { pickBy } from 'lodash-es';
import { useMemo } from 'react';
import { useController, useWatch, type FieldValues, type UseControllerProps } from 'react-hook-form';

interface FormShippingAddressFieldProps<T extends FieldValues> extends UseControllerProps<T> {
    placeholder?: string;
}

export const getGridSalesDraftAddress = (
    address: CustomerAddressDTO,
): co_getplato_grid_api_domain_Address => {
    return {
        address_id: address.addressId,
        street: address.street,
        city: address.city,
        country: address.country,
        country_code: address.countryCode,
        line1: address.addressLine1,
        line2: address.addressLine2,
        line3: address.addressLine3,
        zip: address.zipCode,
    };
};

export const FormShippingAddressField = <T extends FieldValues>(props: FormShippingAddressFieldProps<T>) => {
    const { t } = useLocale('sales-draft');
    const { field } = useController(props);

    const value = field.value as co_getplato_grid_api_domain_Address;

    const customerId = useWatch({
        name: 'customer_id',
    });

    const { data: rawData, isLoading } = api.platoUser.getManyCustomerAddress.get.useQuery({
        customerId: customerId ?? '',
        types: [SrCustomerAddressType.SHIPPING],
    });

    const data = useMemo(() => {
        return rawData?.map((item) => getGridSalesDraftAddress(item));
    }, [rawData]);

    const groupedItems = useMemo(() => {
        return [
            {
                id: '1',
                items: data || [],
            },
        ];
    }, [data]);

    return (
        <SearchCombobox<co_getplato_grid_api_domain_Address>
            className="w-full"
            isLoading={isLoading}
            groupedItems={groupedItems}
            notFoundPlaceholder={''}
            inputPlaceholder={t('sales_draft__header__shipping_address', {
                ns: 'sales-draft',
            })}
            keySelector={(item) => item.address_id || ''}
            valueSelector={(item) => item.address_id || ''}
            renderItem={(item) => {
                return !!item
                    ? getFullAddressString({
                          addressLine1: item.line1,
                          addressLine2: item.line2,
                          addressLine3: item.line3,
                          street: item.street,
                          zipCode: item.zip,
                          city: item.city,
                          country: item.country,
                      })
                    : '';
            }}
            onValueChange={(id) => {
                const address = data?.find((item) => item.address_id === id);

                field.onChange(pickBy(address, Boolean));
            }}
            selectedValue={value?.address_id || ''}
            disabled={field.disabled}
        />
    );
};
